import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { SeoService } from '../../services/seo.service';
import { EnvironmentService } from '../../services/environment.service';



@Component({
  selector: 'app-license-upgrade',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule],
  templateUrl: './license-upgrade.component.html',
  styleUrls: ['./license-upgrade.component.scss'],
})
export class LicenseUpgradeComponent implements OnInit {
  // Signup form data
  signupForm = {
    name: '',
    email: '',
    phone: '',
    vcardType: 'individual', // 'individual', 'business', or 'agency'
    cardCount: 1
  };

  // Loading states
  isProcessingPayment = false;

  // Available card count options based on vCard type
  get availableCardCounts() {
    if (this.signupForm.vcardType === 'individual') {
      return [{ count: 1, display: '1 Card' }]; // Fixed to 1 for individuals
    } else if (this.signupForm.vcardType === 'business') {
      const counts = [10, 25, 50, 100, 200, 300, 500, 750, 1000];
      return counts.map(count => ({
        count,
        display: this.getBusinessCardDisplay(count)
      }));
    } else if (this.signupForm.vcardType === 'agency') {
      return [{ count: 1000, display: '1000 Cards' }]; // Fixed to 1000 for agencies
    }
    return [{ count: 1, display: '1 Card' }];
  }

  // Pricing configuration
  private readonly INDIVIDUAL_ORIGINAL_PRICE = 3999; // From env file
  private readonly BUSINESS_ORIGINAL_PRICE = 999;
  private readonly AGENCY_ORIGINAL_PRICE = 499;

  private readonly BUSINESS_SELLING_PRICE = 999;
  private readonly AGENCY_SELLING_PRICE = 499; // Agency pricing
  private readonly INDIVIDUAL_SELLING_PRICE = 3999;





  constructor(
    private seoService: SeoService,
    private envService: EnvironmentService,
    private router: Router,
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    this.seoService.updatePageSEO('payment');
  }

  // Format number according to Indian currency format
  formatIndianCurrency(amount: number): string {
    if (amount === 0) return '0';
    
    const numStr = amount.toString();
    const lastThree = numStr.substring(numStr.length - 3);
    const otherNumbers = numStr.substring(0, numStr.length - 3);
    
    if (otherNumbers !== '') {
      return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
    } else {
      return lastThree;
    }
  }

  // Centralized business card discount calculation
  calculateBusinessCardPrice(cardCount: number): number {
    const basePrice = this.BUSINESS_SELLING_PRICE; // ₹999
    
    // Define pricing tiers with bulk discounts - 1000 cards at ₹499
    const pricingTiers = [
      { minCount: 10, price: 999, discount: 0 },
      { minCount: 25, price: 949, discount: 5 },
      { minCount: 50, price: 899, discount: 10 },
      { minCount: 100, price: 849, discount: 15 },
      { minCount: 200, price: 799, discount: 20 },
      { minCount: 300, price: 749, discount: 25 },
      { minCount: 500, price: 649, discount: 35 },
      { minCount: 750, price: 549, discount: 45 },
      { minCount: 1000, price: 499, discount: 50 }
    ];
    
    // Find the appropriate pricing tier
    let applicablePrice = basePrice;
    
    for (let i = pricingTiers.length - 1; i >= 0; i--) {
      if (cardCount >= pricingTiers[i].minCount) {
        applicablePrice = pricingTiers[i].price;
        break;
      }
    }
    
    return applicablePrice;
  }

  // Get business card display with discount percentage
  getBusinessCardDisplay(count: number): string {
    const discountedPrice = this.calculateBusinessCardPrice(count);
    const originalPrice = this.BUSINESS_ORIGINAL_PRICE;
    const discountPercent = Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
    
    if (discountPercent > 0) {
      return `${count} Cards - ₹${this.formatIndianCurrency(discountedPrice)}/card (${discountPercent}% off)`;
    } else {
      return `${count} Cards - ₹${this.formatIndianCurrency(discountedPrice)}/card`;
    }
  }



  // Get pack details with GST calculation
  get packDetails() {
    const { currencySymbol } = this.envService.pricing.basicPlan;

    // Calculate pricing based on vCard type and card count
    const basePrice = this.getPackPrice(null, this.signupForm.vcardType);
    const totalPrice = basePrice * this.signupForm.cardCount;
    const gst = Math.round(totalPrice * 0.18); // 18% GST
    const total = totalPrice + gst;

    // Calculate savings if there's a discount
    const originalPrice = this.getOriginalPrice(this.signupForm.vcardType);
    const originalTotal = originalPrice * this.signupForm.cardCount;
    const savings = originalTotal - totalPrice;

    return {
      id: `${this.signupForm.vcardType}-${this.signupForm.cardCount}`,
      name: `${this.signupForm.vcardType.charAt(0).toUpperCase() + this.signupForm.vcardType.slice(1)} vCard`,
      quantity: this.signupForm.cardCount,
      currencySymbol,
      price: basePrice,
      originalPrice: originalPrice,
      discountedPrice: totalPrice,
      savings: savings,
      gst,
      total,
      features: this.getPackFeatures(this.signupForm.vcardType, this.signupForm.cardCount),
    };
  }

  // Get original price for display
  getOriginalPrice(vcardType: string): number {
    if (vcardType === 'individual') {
      return this.INDIVIDUAL_ORIGINAL_PRICE; // ₹3999
    } else if (vcardType === 'business') {
      return this.BUSINESS_ORIGINAL_PRICE; // ₹999
    } else if (vcardType === 'agency') {
      return this.AGENCY_ORIGINAL_PRICE; // ₹499
    }
    return 0;
  }





  getPackFeatures(type: string, quantity: number): string[] {
    const baseFeatures = [
      'Professional Digital Visiting Cards',
      'QR Code for Easy Sharing',
      'Contact Information Display',
      'Social Media Links Integration',
      'Mobile Responsive Design',
      'Download & Print Ready',
      'Customer Support',
    ];

    if (type === 'individual') {
      return [
        ...baseFeatures,
        'Personal Branding',
        'Basic Analytics',
        'Contact Export',
      ];
    } else if (type === 'business') {
      return [
        ...baseFeatures,
        'Company Branding',
        'Team Management',
        'Analytics & Lead Tracking',
        'Bulk Operations',
        ...(quantity >= 50 ? ['Priority Support', 'Custom Templates'] : []),
      ];
    } else if (type === 'agency') {
      return [
        ...baseFeatures,
        'Wholesale Business Cards',
        'Agency Portal Access',
        'Multi-client Management',
        'Revenue Tracking',
        'White-label Solutions',
        'Dedicated Support',
        `Profit Margin: ₹${this.formatIndianCurrency(799)} per card (Sell at ₹${this.formatIndianCurrency(999)})`,
      ];
    }

    return baseFeatures;
  }

  validateForm(): boolean {
    const isBasicValid = !!(
      this.signupForm.name.trim() &&
      this.signupForm.email.trim() &&
      this.signupForm.phone.trim() &&
      this.isValidEmail(this.signupForm.email) &&
      this.isValidPhone(this.signupForm.phone) &&
      this.signupForm.vcardType &&
      this.signupForm.cardCount > 0
    );

    return isBasicValid;
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone: string): boolean {
    const phoneRegex = /^[+]?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  onVCardTypeChange(type: 'individual' | 'business' | 'agency'): void {
    this.signupForm.vcardType = type;

    // Set default card count based on type
    if (type === 'individual') {
      this.signupForm.cardCount = 1; // Fixed to 1 for individuals
    } else if (type === 'business') {
      this.signupForm.cardCount = 10; // Start with 10 for business
    } else if (type === 'agency') {
      this.signupForm.cardCount = 1000; // Fixed to 1000 for agencies
    }

    // Update pricing based on new type
    this.updatePackPricing();
  }

  onCardCountChange(count: number): void {
    this.signupForm.cardCount = count;
    this.updatePackPricing();
  }

  getPackPrice(pack: any, vcardType: string): number {
    // Get base price for the pack based on vCard type
    if (vcardType === 'individual') {
      return this.INDIVIDUAL_SELLING_PRICE; // ₹999 for individuals
    } else if (vcardType === 'business') {
      return this.calculateBusinessCardPrice(this.signupForm.cardCount);
    } else if (vcardType === 'agency') {
      return this.AGENCY_SELLING_PRICE; // ₹200 for agencies
    }
    return this.BUSINESS_SELLING_PRICE; // Fallback
  }

  updatePackPricing(): void {
    // The packDetails getter will automatically calculate GST and total
    // based on the current signupForm values
    console.log(
      'Pack pricing updated for:',
      this.signupForm.vcardType,
      'cards:',
      this.signupForm.cardCount
    );
  }

  async processPayment() {
    if (!this.validateForm()) {
      alert('Please fill in all required fields with valid information.');
      return;
    }

    this.isProcessingPayment = true;

    try {
      await this.initiatePayment();
    } catch (error) {
      console.error('Payment failed:', error);
      alert('Payment failed. Please try again.');
    } finally {
      this.isProcessingPayment = false;
    }
  }

  private async initiatePayment() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const pack = this.packDetails;
    if (!pack) {
      throw new Error('No pack selected');
    }

    // Prepare payment data
    const paymentData = {
      userDetails: this.signupForm,
      packDetails: {
        id: pack.id,
        name: pack.name,
        quantity: pack.quantity,
        price: pack.discountedPrice,
        gst: pack.gst,
        total: pack.total,
      },
      redirectUrl: window.location.origin + '/payment-success', // You can customize this
      cancelUrl: window.location.origin + '/payment-cancel',
    };

    try {
      // Call payment API to create payment token
      const response = await this.http
        .post<any>(`${this.envService.paymentApiUrl}/create`, paymentData)
        .toPromise();

      if (response && response.paymentUrl) {
        // Redirect to payment gateway
        window.location.href = response.paymentUrl;
      } else if (response && response.token) {
        // If you get a token, you might need to handle Razorpay integration here
        this.handleRazorpayPayment(response.token, paymentData);
      } else {
        throw new Error('Invalid payment response');
      }
    } catch (error) {
      console.error('Payment API error:', error);
      // Fallback to direct web app redirect for now
      alert(
        'Payment processing temporarily unavailable. Redirecting to web platform...'
      );
      window.open(this.envService.webAppUrl, '_blank');
    }
  }

  private handleRazorpayPayment(token: string, paymentData: any) {
    // This method would handle Razorpay integration if needed
    // For now, redirect to web platform
    console.log('Razorpay token received:', token);
    window.open(this.envService.webAppUrl, '_blank');
  }

  openWebPlatformDirectly() {
    if (isPlatformBrowser(this.platformId)) {
      window.open(this.envService.webAppUrl, '_blank');
    }
  }

  goBack() {
    this.router.navigate(['/']);
  }
}